import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNodesState } from 'reactflow';
import * as sessionService from '../../services/sessionService';
import { useTheme } from '../../contexts/ThemeContext';
import { useWorkflowNotifications } from '../../utils/notifications';
import ErrorBoundary from '../common/ErrorBoundary';
import { WorkflowCanvas } from './WorkflowCanvas';
import { WorkflowCanvasMode } from './types';

export enum WorkflowMode {
  VIEW = 'view',
  EDIT = 'edit',
  EXECUTE = 'execute'
}

interface WorkflowComponentProps {
  workflowId?: string;
  mode: WorkflowMode;
  initialData?: any;
  onSave?: (workflow: any) => void;
  executionId?: string;
  sessionId?: string;
  height?: string;
}

export const WorkflowComponent: React.FC<WorkflowComponentProps> = ({
  workflowId,
  mode,
  initialData,
  onSave,
  executionId,
  sessionId,
  height = '600px'
}) => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const workflowNotifications = useWorkflowNotifications();

  const [workflow, setWorkflow] = useState<any>(initialData);
  const [executionData, setExecutionData] = useState<any>(null);
  const [loading, setLoading] = useState(!initialData && !!workflowId);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  
  // Add nodes state for immediate visual updates
  const [nodes, setNodes] = useNodesState([]);

  // Load workflow data if not provided and we have an ID
  useEffect(() => {
    const fetchWorkflow = async () => {
      if (!workflowId || initialData) return;

      try {
        setLoading(true);
        console.log(`Fetching workflow data for ID: ${workflowId}`);
        const data = await sessionService.getWorkflow(workflowId);
        console.log('Loaded workflow data:', data);
        setWorkflow(data);
      } catch (err) {
        console.error('Failed to fetch workflow:', err);
        setError("Failed to load workflow data");
      } finally {
        setLoading(false);
      }
    };

    // If we're creating a new workflow (no workflowId and no initialData)
    // Make sure we start with a completely fresh state
    if (!workflowId && !initialData) {
      console.log('Creating new workflow with fresh state');
      const defaultName = `New Workflow - ${new Date().toLocaleDateString()}`;
      setWorkflow({
        name: defaultName,
        description: '',
        phases: [],
        connections: [],
        nodes: []
      });
      setLoading(false);
    } else {
      fetchWorkflow();
    }
  }, [workflowId, initialData]);

  // Load execution data if in execute mode
  useEffect(() => {
    const fetchExecutionData = async () => {
      if (!executionId || mode !== WorkflowMode.EXECUTE) return;

      try {
        console.log(`Fetching execution data for ID: ${executionId}`);
        const data = await sessionService.getWorkflowExecution(executionId);
        console.log('Loaded execution data:', data);
        setExecutionData(data);
      } catch (err) {
        console.error('Failed to fetch execution data:', err);
      }
    };

    fetchExecutionData();

    // Set up polling for execution updates
    if (mode === WorkflowMode.EXECUTE) {
      const interval = setInterval(fetchExecutionData, 5000);
      return () => clearInterval(interval);
    }
  }, [executionId, mode]);

  // No auto-saving state needed

  // Handle workflow changes
  const handleWorkflowChange = (change: any) => {
    console.log(`Workflow change: ${change.type}`);
    
    if (change.type === 'replace_workflow') {
      // Handle complete workflow replacement from chat
      console.log('🔄 HANDLING REPLACE_WORKFLOW:', change.workflow);
      setWorkflow(prevWorkflow => {
        if (!change.workflow) {
          console.error('❌ REPLACE_WORKFLOW: No workflow provided in change');
          return prevWorkflow;
        }
        
        console.log('🔄 REPLACE_WORKFLOW: Replacing entire workflow');
        console.log('🔄 REPLACE_WORKFLOW: New phases:', change.workflow.phases?.length || 0);
        console.log('🔄 REPLACE_WORKFLOW: New nodes:', change.workflow.nodes?.length || 0);
        console.log('🔄 REPLACE_WORKFLOW: New connections:', change.workflow.connections?.length || 0);
        
        // Return the complete new workflow
        return {
          ...change.workflow,
          // Preserve the original ID if it exists
          id: prevWorkflow?.id || change.workflow.id
        };
      });
    }
    else if (change.type === 'update_phase') {
      
      // Update the workflow state
      setWorkflow(prevWorkflow => {
        if (!prevWorkflow) return prevWorkflow;
        
        // Create a deep copy of the workflow with all arrays properly copied
        const updatedWorkflow = {
          ...prevWorkflow,
          phases: [...(prevWorkflow.phases || [])],
          nodes: [...(prevWorkflow.nodes || [])],
          connections: [...(prevWorkflow.connections || [])]
        };
        
        // Find and update the phase
        updatedWorkflow.phases = updatedWorkflow.phases.map(phase => {
          if (phase.id === change.phaseId) {
            return {
              ...phase,
              ...(change.updates.type && { type: change.updates.type }),
              ...(change.updates.label && { label: change.updates.label }),
              ...(change.updates.hasOwnProperty('isAutoLabel') && { isAutoLabel: change.updates.isAutoLabel }),
              ...(change.updates.hasOwnProperty('expanded') && { expanded: change.updates.expanded }),
              ...(change.updates.position && { position: { ...change.updates.position } }),
              ...(change.updates.config && { 
                config: { ...(phase.config || {}), ...change.updates.config } 
              })
            };
          }
          return phase;
        });
        
        return updatedWorkflow;
      });
    }
    else if (change.type === 'add_node') {
      // Keep detailed position logs for debugging
      console.log(`📍 ADD_NODE[${change.node.id}] POSITION:`, JSON.stringify(change.node.position, null, 2));
      
      // Handle adding a new node
      setWorkflow(prevWorkflow => {
        if (!prevWorkflow) return prevWorkflow;
        
        // Create a deep copy of the workflow with all arrays properly copied
        const updatedWorkflow = {
          ...prevWorkflow,
          phases: [...(prevWorkflow.phases || [])],
          nodes: [...(prevWorkflow.nodes || [])],
          connections: [...(prevWorkflow.connections || [])]
        };
        
        // Check if this is a phase node
        if (change.node.type === 'phase') {
          // Make sure phases array exists
          if (!Array.isArray(updatedWorkflow.phases)) {
            updatedWorkflow.phases = [];
          }
          
          // Ensure position is properly copied
          const position = change.node.position ? { ...change.node.position } : { x: 0, y: 0 };
          console.log(`📍 Adding phase ${change.node.id} with position:`, JSON.stringify(position, null, 2));
          
          // Add the new phase to the phases array
          updatedWorkflow.phases.push({
            id: change.node.id,
            type: change.node.data.type,
            label: change.node.data.label,
            position: position,
            config: change.node.data.config || {}
          });
        } else {
          // For entities (tool, persona, artifact, etc.), add to the nodes array
          if (!Array.isArray(updatedWorkflow.nodes)) {
            updatedWorkflow.nodes = [];
          }
          
          updatedWorkflow.nodes.push({
            id: change.node.id,
            type: change.node.type,
            position: change.node.position,
            data: { ...change.node.data }
          });
        }
        
        // Ensure connections array exists and is preserved
        if (!updatedWorkflow.connections) {
          updatedWorkflow.connections = [];
        }
        
        return updatedWorkflow;
      });
    }
    else if (change.type === 'add_tool_node' || change.type === 'add_persona_node' || change.type === 'add_artifact_node') {
      // Handle adding tool, persona, or artifact nodes
      console.log(`🔄 HANDLING ${change.type}:`, change);
      setWorkflow(prevWorkflow => {
        if (!prevWorkflow) return prevWorkflow;
        
        // Create a deep copy of the workflow with all arrays properly copied
        const updatedWorkflow = {
          ...prevWorkflow,
          phases: [...(prevWorkflow.phases || [])],
          nodes: [...(prevWorkflow.nodes || [])],
          connections: [...(prevWorkflow.connections || [])]
        };
        
        // Make sure nodes array exists
        if (!updatedWorkflow.nodes) {
          updatedWorkflow.nodes = [];
        }
        
        // Validate the position before adding
        if (!change.node.position || typeof change.node.position.x !== 'number' || typeof change.node.position.y !== 'number') {
          console.error(`❌ INVALID POSITION for new ${change.type}:`, change.node.position);
        }
        
        // Create a deep copy of the position to avoid reference issues
        const position = {
          x: Number(change.node.position.x),
          y: Number(change.node.position.y)
        };
        
        console.log(`📍 Adding new ${change.node.type} node with position:`, position);
        
        // Add the new node with the validated position
        updatedWorkflow.nodes.push({
          id: change.node.id,
          type: change.node.type,
          position: position,
          data: change.node.data
        });
        
        // Ensure connections array exists and is preserved
        if (!updatedWorkflow.connections) {
          updatedWorkflow.connections = [];
        }
        
        // Log the updated workflow for debugging
        console.log("🔄 UPDATED WORKFLOW AFTER ADDING ENTITY NODE:", {
          phases: updatedWorkflow.phases?.length || 0,
          connections: updatedWorkflow.connections?.length || 0,
          nodes: updatedWorkflow.nodes?.length || 0,
          fullConnections: JSON.stringify(updatedWorkflow.connections)
        });
        
        return updatedWorkflow;
      });
      
      // Log the updated workflow state after the change
      setTimeout(() => {
        console.log("🔄 UPDATED WORKFLOW STATE AFTER CHANGE:", {
          phases: workflow?.phases?.length || 0,
          connections: workflow?.connections?.length || 0,
          nodes: workflow?.nodes?.length || 0,
          fullConnections: JSON.stringify(workflow?.connections)
        });
      }, 0);
    }
    else if (change.type === 'update_tool') {
      // Handle updating tool nodes
      setWorkflow(prevWorkflow => {
        if (!prevWorkflow) return prevWorkflow;
        
        // Create a deep copy of the workflow with all arrays properly copied
        const updatedWorkflow = {
          ...prevWorkflow,
          phases: [...(prevWorkflow.phases || [])],
          nodes: [...(prevWorkflow.nodes || [])],
          connections: [...(prevWorkflow.connections || [])]
        };
        
        // Find and update the tool node
        if (updatedWorkflow.nodes) {
          updatedWorkflow.nodes = updatedWorkflow.nodes.map(node => {
            if (node.id === change.toolNodeId) {
              // Create a deep copy of the node to avoid reference issues
              const updatedNode = {
                ...node,
                data: {
                  ...node.data,
                  // Merge in the updates
                  ...change.updates
                }
              };
              
              // Explicitly update the position if provided
              if (change.updates.position) {
                updatedNode.position = { 
                  x: Number(change.updates.position.x),
                  y: Number(change.updates.position.y)
                };
              } else if (node.position) {
                // Preserve existing position
                updatedNode.position = { 
                  x: Number(node.position.x),
                  y: Number(node.position.y)
                };
              }
              
              console.log('🔧 TOOL-UPDATE: Updated tool node data:', updatedNode.data);
              return updatedNode;
            }
            return node;
          });
        }
        
        // Ensure connections array exists and is preserved
        if (!updatedWorkflow.connections) {
          updatedWorkflow.connections = [];
        }
        
        return updatedWorkflow;
      });
    }
    else if (change.type === 'update_persona') {
      console.log('🔄 HANDLING update_persona:', change);
      
      setWorkflow(prevWorkflow => {
        if (!prevWorkflow) return prevWorkflow;
        
        const updatedWorkflow = {
          ...prevWorkflow,
          phases: [...(prevWorkflow.phases || [])],
          nodes: [...(prevWorkflow.nodes || [])],
          connections: [...(prevWorkflow.connections || [])]
        };
        
        if (updatedWorkflow.nodes) {
          updatedWorkflow.nodes = updatedWorkflow.nodes.map(node => {
            if (node.id === change.personaNodeId) {
              // Create new data object preserving ALL existing data including callbacks
              const updatedNode = {
                ...node,
                // Update position if provided
                ...(change.updates.position && {
                  position: { 
                    x: Number(change.updates.position.x), 
                    y: Number(change.updates.position.y) 
                  }
                }),
                data: {
                  ...node.data, // Preserve ALL existing data including callbacks
                  // Only add updates that are actually provided
                  ...(change.updates.personaId && { personaId: change.updates.personaId }),
                  ...(change.updates.personaName && { personaName: change.updates.personaName }),
                  ...(change.updates.personaSpecialization && { personaSpecialization: change.updates.personaSpecialization }),
                  ...(change.updates.personaDescription && { personaDescription: change.updates.personaDescription }),
                  ...(change.updates.personaData && { personaData: change.updates.personaData })
                }
              };
              
              console.log('🔄 PERSONA-UPDATE: Updated persona node data:', updatedNode.data);
              return updatedNode;
            }
            return node;
          });
        }
        
        return updatedWorkflow;
      });
    }
    else if (change.type === 'add_connection') {
      // Handle adding a connection between nodes
      setWorkflow(prevWorkflow => {
        if (!prevWorkflow) return prevWorkflow;
        
        // Create a deep copy of the workflow with all arrays properly copied
        const updatedWorkflow = {
          ...prevWorkflow,
          phases: [...(prevWorkflow.phases || [])],
          nodes: [...(prevWorkflow.nodes || [])],
          connections: [...(prevWorkflow.connections || [])]
        };
        
        // Ensure connections array exists
        if (!updatedWorkflow.connections) {
          updatedWorkflow.connections = [];
        }
        
        // Add the new connection
        updatedWorkflow.connections.push({
          source: change.source,
          target: change.target,
          sourceHandle: change.sourceHandle,
          targetHandle: change.targetHandle,
          dependencyType: change.dependencyType || 'flow'
        });
        
        console.log("🔄 ADDED CONNECTION:", {
          source: change.source,
          target: change.target,
          sourceHandle: change.sourceHandle,
          targetHandle: change.targetHandle
        });
        
        return updatedWorkflow;
      });
    }
    else if (change.type === 'add_tool_to_phase') {
      console.log("🔄 HANDLING ADD_TOOL_TO_PHASE:", change);
      // Handle adding a tool to a phase
      setWorkflow(prevWorkflow => {
        if (!prevWorkflow) return prevWorkflow;
        
        // Create a deep copy of the workflow with all arrays properly copied
        const updatedWorkflow = {
          ...prevWorkflow,
          phases: [...(prevWorkflow.phases || [])],
          nodes: [...(prevWorkflow.nodes || [])],
          connections: [...(prevWorkflow.connections || [])]
        };
        
        // Find the tool node to get the tool ID
        const toolNode = updatedWorkflow.nodes?.find(node => node.id === change.toolNodeId);
        // Use the provided toolId or fallback to the node ID if toolId is not set
        const toolId = change.toolId || toolNode?.data?.toolId || change.toolNodeId;
        
        // Find and update the phase
        if (updatedWorkflow.phases) {
          updatedWorkflow.phases = updatedWorkflow.phases.map(phase => {
            if (phase.id === change.phaseId) {
              // Make sure config and tools exist
              const config = phase.config || {};
              const tools = config.tools || [];
              
              // Add the tool if it's not already in the list
              if (!tools.includes(toolId)) {
                return {
                  ...phase,
                  config: {
                    ...config,
                    tools: [...tools, toolId]
                  }
                };
              }
            }
            return phase;
          });
        }
        
        // Add the connection to the connections array
        if (!updatedWorkflow.connections) {
          updatedWorkflow.connections = [];
        }
    
        updatedWorkflow.connections.push({
          source: change.toolNodeId,
          target: change.phaseId,
          sourceHandle: 'tool_output',
          targetHandle: 'tools',
          dependencyType: 'tool_assignment'
        });
        
        return updatedWorkflow;
      });
    }
    else if (change.type === 'add_persona_to_phase') {
      // Handle adding a persona to a phase
      setWorkflow(prevWorkflow => {
        if (!prevWorkflow) return prevWorkflow;
        
        // Create a deep copy of the workflow with all arrays properly copied
        const updatedWorkflow = {
          ...prevWorkflow,
          phases: [...(prevWorkflow.phases || [])],
          nodes: [...(prevWorkflow.nodes || [])],
          connections: [...(prevWorkflow.connections || [])]
        };
        
        // Find the persona node to get the persona ID
        const personaNode = updatedWorkflow.nodes?.find(node => node.id === change.personaNodeId);
        const personaId = personaNode?.data?.personaId;
        
        console.log(`DETAILED DEBUG: Adding persona ${personaId} to phase ${change.phaseId}`);
        console.log(`DETAILED DEBUG: Persona node:`, personaNode);
        
        if (!personaId) {
          console.error('Persona ID not found');
          return prevWorkflow;
        }
        
        // Find and update the phase
        if (updatedWorkflow.phases) {
          updatedWorkflow.phases = updatedWorkflow.phases.map(phase => {
            if (phase.id === change.phaseId) {
              // Make sure config and personas exist
              const config = phase.config || {};
              const personas = config.personas || [];
              
              // Check if this persona is already assigned
              const existingAssignment = personas.find(p => p.persona_id === personaId);
              
              console.log(`DETAILED DEBUG: Existing personas for phase ${phase.id}:`, personas);
              
              if (!existingAssignment) {
                // Add the persona assignment with full persona data
                const updatedPhase = {
                  ...phase,
                  config: {
                    ...config,
                    personas: [
                      ...personas,
                      {
                        persona_id: personaId,
                        role: change.role || 'Contributor',
                        persona_data: change.personaData // Store full persona data
                      }
                    ]
                  }
                };
                
                console.log(`DETAILED DEBUG: Updated phase config:`, updatedPhase.config);
                return updatedPhase;
              }
            }
            return phase;
          });
        }
        
        // Add the connection to the connections array
        if (!updatedWorkflow.connections) {
          updatedWorkflow.connections = [];
        }
    
        updatedWorkflow.connections.push({
          source: change.personaNodeId,
          target: change.phaseId,
          sourceHandle: 'persona_output',
          targetHandle: 'personas',
          dependencyType: 'persona_assignment'
        });
        
        return updatedWorkflow;
      });
    }
    else if (change.type === 'update_artifact') {
      // Handle updating artifact nodes
      setWorkflow(prevWorkflow => {
        if (!prevWorkflow) return prevWorkflow;
        
        // Create a deep copy of the workflow with all arrays properly copied
        const updatedWorkflow = {
          ...prevWorkflow,
          phases: [...(prevWorkflow.phases || [])],
          nodes: [...(prevWorkflow.nodes || [])],
          connections: [...(prevWorkflow.connections || [])]
        };
        
        // Find and update the artifact node
        if (updatedWorkflow.nodes) {
          updatedWorkflow.nodes = updatedWorkflow.nodes.map(node => {
            if (node.id === change.artifactNodeId) {
              // Create a deep copy of the node to avoid reference issues
              const updatedNode = {
                ...node,
                data: {
                  ...node.data,
                  // Merge in the updates
                  ...change.updates
                }
              };
              
              // Explicitly update the position if provided
              if (change.updates.position) {
                updatedNode.position = { 
                  x: Number(change.updates.position.x),
                  y: Number(change.updates.position.y)
                };
              } else if (node.position) {
                // Preserve existing position
                updatedNode.position = { 
                  x: Number(node.position.x),
                  y: Number(node.position.y)
                };
              }
              
              console.log('🔧 ARTIFACT-UPDATE: Updated artifact node data:', updatedNode.data);
              return updatedNode;
            }
            return node;
          });
        }
        
        // Ensure connections array exists and is preserved
        if (!updatedWorkflow.connections) {
          updatedWorkflow.connections = [];
        }
        
        return updatedWorkflow;
      });
    }
    else if (change.type === 'add_input_artifacts_to_phase') {
      // Handle adding input artifacts to a phase
      setWorkflow(prevWorkflow => {
        if (!prevWorkflow) return prevWorkflow;
        
        // Create a deep copy of the workflow with all arrays properly copied
        const updatedWorkflow = {
          ...prevWorkflow,
          phases: [...(prevWorkflow.phases || [])],
          nodes: [...(prevWorkflow.nodes || [])],
          connections: [...(prevWorkflow.connections || [])]
        };
        
        // Find the artifact node to get the artifacts
        const artifactNode = updatedWorkflow.nodes?.find(node => node.id === change.artifactNodeId);
        const artifacts = artifactNode?.data?.selectedArtifacts || [];
        
        if (artifacts.length === 0) {
          console.error('No artifacts selected');
          return prevWorkflow;
        }
        
        // Find and update the phase
        if (updatedWorkflow.phases) {
          updatedWorkflow.phases = updatedWorkflow.phases.map(phase => {
            if (phase.id === change.phaseId) {
              // Make sure config and input_artifacts exist
              const config = phase.config || {};
              const inputArtifacts = config.input_artifacts || [];
              
              // Add the artifacts if they're not already in the list
              const newArtifacts = artifacts.filter(
                artifact => !inputArtifacts.some(a => a.id === artifact.id)
              );
              
              if (newArtifacts.length > 0) {
                return {
                  ...phase,
                  config: {
                    ...config,
                    input_artifacts: [...inputArtifacts, ...newArtifacts]
                  }
                };
              }
            }
            return phase;
          });
        }
        
        // Add the connection to the connections array
        if (!updatedWorkflow.connections) {
          updatedWorkflow.connections = [];
        }
    
        updatedWorkflow.connections.push({
          source: change.artifactNodeId,
          target: change.phaseId,
          sourceHandle: 'artifact_output',
          targetHandle: 'artifacts_in',
          dependencyType: 'input_artifact'
        });
        
        return updatedWorkflow;
      });
    }
    else if (change.type === 'add_output_artifacts_to_phase') {
      // Handle adding output artifacts to a phase
      setWorkflow(prevWorkflow => {
        if (!prevWorkflow) return prevWorkflow;
        
        // Create a deep copy of the workflow with all arrays properly copied
        const updatedWorkflow = {
          ...prevWorkflow,
          phases: [...(prevWorkflow.phases || [])],
          nodes: [...(prevWorkflow.nodes || [])],
          connections: [...(prevWorkflow.connections || [])]
        };
        
        // Find the artifact node to get the artifacts
        const artifactNode = updatedWorkflow.nodes?.find(node => node.id === change.artifactNodeId);
        const artifacts = artifactNode?.data?.selectedArtifacts || [];
        
        if (artifacts.length === 0) {
          console.error('No artifacts selected');
          return prevWorkflow;
        }
        
        // Find and update the phase
        if (updatedWorkflow.phases) {
          updatedWorkflow.phases = updatedWorkflow.phases.map(phase => {
            if (phase.id === change.phaseId) {
              // Make sure config and output_artifacts exist
              const config = phase.config || {};
              const outputArtifacts = config.output_artifacts || [];
              
              // Add the artifacts if they're not already in the list
              const newArtifacts = artifacts.filter(
                artifact => !outputArtifacts.some(a => a.id === artifact.id)
              );
              
              if (newArtifacts.length > 0) {
                return {
                  ...phase,
                  config: {
                    ...config,
                    output_artifacts: [...outputArtifacts, ...newArtifacts]
                  }
                };
              }
            }
            return phase;
          });
        }
        
        // Add the connection to the connections array
        if (!updatedWorkflow.connections) {
          updatedWorkflow.connections = [];
        }
    
        updatedWorkflow.connections.push({
          source: change.phaseId,
          target: change.artifactNodeId,
          sourceHandle: 'artifacts_out',
          targetHandle: 'artifact_input',
          dependencyType: 'output_artifact'
        });
        
        return updatedWorkflow;
      });
    }
    else if (change.type === 'delete_node') {
      console.log(`🗑️ HANDLING DELETE_NODE: ${change.nodeId}`);
      
      setWorkflow(prevWorkflow => {
        if (!prevWorkflow) return prevWorkflow;
        
        // Create a deep copy of the workflow
        const updatedWorkflow = {
          ...prevWorkflow,
          phases: [...(prevWorkflow.phases || [])],
          nodes: [...(prevWorkflow.nodes || [])],
          connections: [...(prevWorkflow.connections || [])]
        };
        
        // Remove the node from phases if it's a phase node
        updatedWorkflow.phases = updatedWorkflow.phases.filter(phase => phase.id !== change.nodeId);
        
        // Remove the node from nodes if it's an entity node
        updatedWorkflow.nodes = updatedWorkflow.nodes.filter(node => node.id !== change.nodeId);
        
        // Remove any connections involving this node
        updatedWorkflow.connections = updatedWorkflow.connections.filter(
          conn => conn.source !== change.nodeId && conn.target !== change.nodeId
        );
        
        console.log(`🗑️ DELETED NODE ${change.nodeId} from workflow data`);
        return updatedWorkflow;
      });
    }
    // No auto-save - changes are only saved when the user clicks the Save button
  };

  // Handle explicit save action
  const handleExplicitSave = () => {
    setIsSaving(true);
    if (workflow) {
      console.log('Explicit save requested for workflow');
      
      if (onSave) {
        // If onSave is provided, let WorkflowCanvas handle the save and callback
        // Don't call onSave here - let WorkflowCanvas call it with the saved workflow
        console.log('onSave prop provided, letting WorkflowCanvas handle the save');
        // The WorkflowCanvas will call onSave with the saved workflow (including ID)
        return; // Don't call onSave here, don't set isSaving to false
      }
      
      // Only save directly if no onSave prop is provided
      sessionService.createWorkflow(workflow)
        .then(savedWorkflow => {
          workflowNotifications.workflowSaved(workflow.name);
          
          // Create a complete workflow object with the ID from the response
          const completeWorkflow = {
            ...workflow,
            id: savedWorkflow.id
          };
          
          if (savedWorkflow && savedWorkflow.id) {
            console.log(`Navigating to workflow view after save: ${savedWorkflow.id}`);
            navigate(`/workflows/${savedWorkflow.id}`);
          } else {
            console.error('No workflow ID returned from save operation');
            // Navigate to the workflows list instead
            navigate('/workflows');
          }
        })
        .catch(error => {
          console.error('Failed to save workflow:', error);
          workflowNotifications.workflowSaveError(error.message || 'Unknown error');
        })
        .finally(() => {
          setIsSaving(false);
        });
    } else {
      setIsSaving(false);
    }
  };

  // Handle workflow save
  const handleWorkflowSave = (savedWorkflow: any) => {
    console.log('Workflow saved:', savedWorkflow);
    
    // Log the positions in the saved workflow with validation
    if (savedWorkflow.phases) {
      console.log('📍 POSITIONS IN SAVED WORKFLOW:');
      savedWorkflow.phases.forEach((phase: any) => {
        logNodePosition('SAVED_WORKFLOW', phase.id, phase.position);
        validatePosition(phase.position, phase.id, 'SAVED_WORKFLOW');
      });
    }
    
    // Check if positions were preserved between nodes and saved workflow
    if (nodes.length > 0 && savedWorkflow.phases) {
      const nodeMap = new Map(nodes.map(node => [node.id, node]));
      
      savedWorkflow.phases.forEach((phase: any) => {
        const node = nodeMap.get(phase.id);
        if (node) {
          if (node.position.x !== phase.position.x || node.position.y !== phase.position.y) {
            console.error(`❌ POSITION MISMATCH for node ${phase.id}:`, {
              node: node.position,
              saved: phase.position
            });
          }
        }
      });
    }
    
    // Only navigate if this was an explicit save action
    if (isSaving) {
      setIsSaving(false);
      
      // Ensure the workflow has an ID
      if (!savedWorkflow.id && savedWorkflow.response && savedWorkflow.response.id) {
        savedWorkflow.id = savedWorkflow.response.id;
      }
      
      console.log('In handleWorkflowSave, savedWorkflow ID:', savedWorkflow.id);
      
      if (onSave) {
        console.log('Calling parent onSave with savedWorkflow ID:', savedWorkflow.id);
        onSave(savedWorkflow);
      }
      
      // Only navigate after explicit saves
      if (savedWorkflow && savedWorkflow.id) {
        console.log(`Navigating to workflow view after explicit save: ${savedWorkflow.id}`);
        workflowNotifications.workflowSaved(workflowName);
        navigate(`/workflows/${savedWorkflow.id}`);
      } else {
        console.error('No workflow ID returned from save operation');
        // Navigate to the workflows list instead
        navigate('/workflows');
      }
    }
  };

  if (loading) return (
    <div className={`p-8 text-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
      <div className="animate-pulse">
        <div className={`h-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} rounded w-1/4 mx-auto mb-4`}></div>
        <div className={`h-8 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} rounded w-1/2 mx-auto mb-4`}></div>
        <div className={`h-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} rounded w-3/4 mx-auto`}></div>
      </div>
      <p className="mt-4">Loading workflow...</p>
    </div>
  );

  if (error) return (
    <div className="p-4 text-red-600 border border-red-300 bg-red-50 rounded-md">
      <h3 className="text-lg font-medium">Error Loading Workflow</h3>
      <p>{error}</p>
      <button
        className="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        onClick={() => window.location.reload()}
      >
        Try Again
      </button>
    </div>
  );

  // Map WorkflowMode to WorkflowCanvasMode
  const getCanvasMode = () => {
    switch (mode) {
      case WorkflowMode.EDIT:
        return WorkflowCanvasMode.EDIT;
      case WorkflowMode.EXECUTE:
        return WorkflowCanvasMode.EXECUTE;
      case WorkflowMode.VIEW:
      default:
        return WorkflowCanvasMode.VIEW;
    }
  };

  // Render the unified canvas for all modes
  return (
    <ErrorBoundary>
      <div className={`${
        mode === WorkflowMode.EDIT 
          ? 'h-full w-full' // Full container size for edit mode
          : 'rounded-lg shadow-md p-4'
      } ${darkMode ? 'bg-gray-800' : 'bg-white'} workflow-updated`}>
        <WorkflowCanvas
          workflowData={workflow}
          mode={getCanvasMode()}
          onWorkflowChange={handleWorkflowChange}
          executionData={executionData}
          height={height}
          readOnly={mode !== WorkflowMode.EDIT}
          onSave={handleExplicitSave}
          fullScreen={mode === WorkflowMode.EDIT} // Enable full screen for edit mode
        />
      </div>
    </ErrorBoundary>
  );
};
